import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject, map, of } from 'rxjs';
import { Match } from '../../_shared/models/match.model';
import { AuthService } from '../../auth/auth.service';
import { TimeItem } from '../../_shared/models/csTypes/time_item';
import { Extension } from '../../_shared/models/csTypes/extension';
import { Venue } from '../../_shared/models/csTypes/venue';
import { Court } from '../../_shared/models/csTypes/court';

export interface MatchAssignments {
    venue?: Venue | undefined;
    court?: Court | undefined;
    _officialsUserId?: string | undefined;
    _tournamentId?: string | undefined;
    _videoBoardIds?: string[];
}

@Injectable({
    providedIn: 'root'
})
export class MatchService {
    private authService = inject(AuthService);
    private http = inject(HttpClient);

    private unfollowObservable$ = new Subject<any>();
    private followObservable$ = new Subject<any>();
    private deleteObservable$ = new Subject<Match>();
    private editObservable$ = new Subject<any>();
    private completedObservable$ = new Subject<Match>();
    private transferObservable$ = new Subject<any>();
    private matchFilterConfig$ = new Subject<any>();
    private scoreUpdated$ = new Subject<any>();
    private addEventRequest$ = new Subject<any>();
    private fullMatchUndo$ = new Subject<any>();
    private autoSwitchScoringTypeOnTieBreak$ = new Subject<any>();
    private trackerChangedScoringType$ = new Subject<any>();

    isTracker$: Observable<boolean>;

    constructor() { }

    /**
     * Get all matches
     */
    index() {
        return this.http.get<any>('api_endpoint' + '/match/index');
    }

    // Owners matches
    getOwnersMatches(paginationConfig, playerId?) {
        return this.http.post<any>('api_endpoint' + '/match/my-matches/', { paginationConfig, playerId });
    }

    // Transfer matches
    getTransferMatches() {
        return this.http.get<any>('api_endpoint' + '/match/transfer-matches/');
    }

    // Transfer match
    transferMatch(transferObj) {
        return this.http.put<any>('api_endpoint' + '/match/transfer/', transferObj);
    }

    // Transfer accept
    acceptTransfer(matchId) {
        return this.http.put<any>('api_endpoint' + '/match/transfer/accept/', { matchId: matchId });
    }

    // Transfer decline
    declineTransfer(matchId) {
        return this.http.put<any>('api_endpoint' + '/match/transfer/decline/', { matchId: matchId });
    }

    /**
     * Get match
     */
    show(matchId) {
        return this.http.get<any>('api_endpoint' + '/match/' + matchId);
    }

    /**
     * Post match
     */
    create(match, policyConfiguration, tieId, trackerId) {
        return this.http.post<any>('api_endpoint' + '/match', { match, policyConfiguration, tieId, trackerId });
    }

    /**
     * Post match
     */
    createPartnerMatch(partner, code) {
        return this.http.post<any>('api_endpoint' + '/match/partner', { partner, code });
    }

    /**
     * Post match
     */
    changeTracker(matchId) {
        return this.http.post<any>('api_endpoint' + '/match/change-tracker', { matchId });
    }

    /**
     * Edit match
     */
    edit(match) {
        return this.http.put<any>('api_endpoint' + '/match/edit', match);
    }

    /**
     * Update match
     */
    update(match, isUndo, notification?) {
        return this.http.put<any>('api_endpoint' + '/match/' + match._id, { match, notification, isUndo });
    }

    startLiveScoreTournamentMatch(match, isUndo: boolean, notification?: any, courtId?: string, venueId?: string) {
        return this.http.put<any>('api_endpoint' + '/match/' + match._id, { match, isUndo, notification, courtId, venueId });
    }

    updateScore(
        payload: {
            matchId: string;
            sets: any;
            score: any;
            isUndo: boolean;
            playerCourtTimeLog: any;
            substituteTrack: any;
            tieScore?: any;
            eventFlags?: {
                isTimeoutStart?: boolean;
                isTimeoutEnd?: boolean;
                isSubstituteStart?: boolean;
                isSubstituteEnd?: boolean;
                isSubstituteUndo?: boolean;
                isTimeoutUndo?: boolean;
                isStartPoint?: boolean;
                isEndPoint?: boolean;
                isBreakEnd?: boolean;
                isBreakStart?: boolean;
                isBreakUndo?: boolean;
            }            
        }
    ) {
        return this.http.put<any>('api_endpoint' + '/match/update-score', payload)
    }

    /**
     * Update weather
     */

    updateWeather(matchId, weatherObj) {
        return this.http.put<any>(`api_endpoint/match/${matchId}/weather`, { weatherObj });
    }

    /**
     * Follow match
     */

    follow(matchId) {
        return this.http.put<any>('api_endpoint' + '/match/follow', { matchId: matchId });
    }

    unfollow(matchId) {
        return this.http.put<any>('api_endpoint' + '/match/unfollow', { MatchId: matchId });
    }

    validateTracker(matchId, code) {
        return this.http.post<any>('api_endpoint' + '/match/validate-tracker', { matchId, code });
    }

    getAccessCode(matchId) {
        return this.http.post<any>('api_endpoint' + '/match/get-access-code', { matchId });
    }

    addExtension(matchId: string, extension: Extension) {
        return this.http.post<any>('api_endpoint' + '/match/add-extension', { matchId, extension });
    }

    /**
     * Delete match
     */
    delete(matchId) {
        return this.http.delete<any>('api_endpoint' + '/match/' + matchId);
    }

    /**
     * Change tracking mode
     */
    changeTrackingMode(matchId, trackingObj) {
        return this.http.put<any>(`api_endpoint/match/${matchId}/change-tracking`, { trackingObj });
    }

    /**
     * Add Scheduled Item - deprecated. Use timeItem instead
     */
    addScheduledItem(scheduledItemObj, matchId) {
        return this.http.put<any>('api_endpoint' + '/match/' + matchId + '/addScheduledItem', { scheduledItemObj });
    }

    /**
     * Add time item
     */
    addTimeItem(matchId, timeItem: TimeItem) {
        return this.http.put<any>('api_endpoint' + '/match/add-time-item', { matchId, timeItem });
    }

    /**
     * Delete time item
     */
    deleteTimeItem(matchId, itemId: string) {
        return this.http.put<any>('api_endpoint' + '/match/delete-time-item', { matchId, itemId });
    }

    /**
     * Update time item
     */
    updateTimeItem(matchId: string, itemId: string, itemValue: any) {
        return this.http.put<any>('api_endpoint' + '/match/update-time-item', { matchId, itemId, itemValue });
    }

    matchAssignments(_matchId, matchAssignments: MatchAssignments): Observable<Match> {
        return this.http.put<any>('api_endpoint' + `/match/${_matchId}/assign`, matchAssignments).pipe(
          map(res => res.data)
        );
      }

    publishFollow(match: Match) {
        this.followObservable$.next(match);
    }

    subscribeFollow(): Observable<any> {
        return this.followObservable$.asObservable();
    }

    publishUnfollow(matchId) {
        this.unfollowObservable$.next(matchId);
    }

    subscribeUnfollow(): Observable<any> {
        return this.unfollowObservable$.asObservable();
    }

    publishDelete(match: Match) {
        this.deleteObservable$.next(match);
    }

    subscribeDelete(): Observable<Match> {
        return this.deleteObservable$.asObservable();
    }

    publishEdit(match) {
        this.editObservable$.next(match);
    }

    subscribeEdit(): Observable<any> {
        return this.editObservable$.asObservable();
    }

    publishTransfer(transferObj) {
        this.transferObservable$.next(transferObj);
    }

    subscribeTransfer(): Observable<any> {
        return this.transferObservable$.asObservable();
    }

    publishMatchFilterConfig(matchFilterConfig) {
        this.matchFilterConfig$.next(matchFilterConfig);
    }

    subscribeMatchFilterConfig(): Observable<any> {
        return this.matchFilterConfig$.asObservable();
    }

    publishMatchCompleted(match: Match) {
        this.completedObservable$.next(match);
    }

    subscribeMatchCompleted(): Observable<Match> {
        return this.completedObservable$.asObservable();
    }

    publishScoreUpdated(match: Match) {
        this.scoreUpdated$.next(match);
    }

    subscribeScoreUpdated(): Observable<Match> {
        return this.scoreUpdated$.asObservable();
    }

    publishAddEventRequest(type: string) {
        // type can be individual or tie
        this.addEventRequest$.next(type);
    }

    subscribeAddEventRequest(): Observable<string> {
        return this.addEventRequest$.asObservable();
    }

   publishFullMatchUndo(payload?: any) {
    this.fullMatchUndo$.next(payload);
    }

    subscribeFullMatchUndo(): Observable<any> {
        return this.fullMatchUndo$.asObservable();
    }


    // This needs to be moved into a pipe
    getWeatherInfo(match: Match) {
        if (match.weather?.main) {
            let temp = match.weather?.main.temp;
            let tempUnit = 'C';
            let windSpeed = match.weather?.wind.speed;
            let windSpeedUnit = 'm/s';
            const humidity = match.weather?.main.humidity;

            if (this.authService.authUserSignal()?.settings.unit === 'imperial') {
                temp = temp * 9 / 5 + 32;
                tempUnit = 'F';
                windSpeed = windSpeed * 2.237;
                windSpeedUnit = 'mph';
            }

            return `${temp.toFixed(0)}${tempUnit} | ${windSpeed.toFixed(1)}${windSpeedUnit} | ${humidity}%`;
        } else {
            return '';
        }

    }

    isTracker(match: Match) {        
        if (this.authService.authUserSignal()?._id && (match?.dist.owner === this.authService.authUserSignal()?._id || match?.trackers?.some(t => t.tracker?._id === this.authService.authUserSignal()?._id))) {
            return true;
        }

        return false;
    }

    autoSwitchScoringTypeOnTieBreak(dataObj) {
        this.autoSwitchScoringTypeOnTieBreak$.next(dataObj)
    }

    subscribeAutoSwitchScoringTypeOnTieBreak(): Observable<any> {
        return this.autoSwitchScoringTypeOnTieBreak$.asObservable();
    }

    publishTrackerChangedScoringType(dataObj) {
        this.trackerChangedScoringType$.next(dataObj);
    }

    subscribeTrackerChangedScoringType(): Observable<any> {
        return this.trackerChangedScoringType$.asObservable();
    }

    getTournamentMatchesByPartner(partner: string, tournamentId: string, date: string, venueId: string | null, page: number, limit: number) {
        return this.http.post<any>('api_endpoint' + `/match/matchup-schedule/${partner}`, { tournamentId, date, venueId, page, limit });
    }
}
